import React, { useEffect, useState } from 'react'
import { Loader2, Table2, X } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import PasteScreenshotZone, { normalizePastedScreenshotFile } from './PasteScreenshotZone'
import { buildEvidenceBulkItem } from '../../../components/EvidenceLinkBrowser'
import { formatEvidenceRowId } from '../../../utils/evidenceBrowserLabels'
import { toDatetimeLocalValue } from '../../../utils/patronActivityTimeline'

const P = { pri: '#0a5169', priM: '#e8f2f6' }

function floorItemLabel(item) {
  if (!item) return ''
  const type = item.type || 'item'
  const egm = item.EgmHouseNo || item.HouseNumber || item.egm_pk || ''
  const id = formatEvidenceRowId(item)
  return `${type}${egm ? ` · EGM ${egm}` : ''} · ${id}`
}

export default function AddActivityObservationModal({
  isOpen,
  onClose,
  caseId,
  defaultObservedAt,
  defaultEgm = '',
  defaultEgmTo = '',
  onBrowseFloorLink,
  onSaved,
}) {
  const [observationText, setObservationText] = useState('')
  const [observedAt, setObservedAt] = useState('')
  const [egm, setEgm] = useState('')
  const [egmTo, setEgmTo] = useState('')
  const [attachmentFiles, setAttachmentFiles] = useState([])
  const [linkedItem, setLinkedItem] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setObservationText('')
    setObservedAt(defaultObservedAt ? toDatetimeLocalValue(defaultObservedAt) : toDatetimeLocalValue(new Date()))
    setEgm(defaultEgm || '')
    setEgmTo(defaultEgmTo || '')
    setAttachmentFiles([])
    setLinkedItem(null)
    setError(null)
  }, [isOpen, defaultObservedAt, defaultEgm, defaultEgmTo])

  if (!isOpen) return null

  const handleSave = async () => {
    if (!caseId) return
    if (!observationText.trim()) {
      setError('Describe what was observed.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('observation_text', observationText.trim())
      if (observedAt) fd.append('observed_at', observedAt)
      if (egm.trim()) fd.append('egm', egm.trim())
      if (egmTo.trim()) fd.append('egm_to', egmTo.trim())
      if (linkedItem) fd.append('linked_item', JSON.stringify(buildEvidenceBulkItem(linkedItem)))
      attachmentFiles.forEach((attachmentFile) => {
        const uploadFile = attachmentFile.type?.startsWith('image/')
          ? normalizePastedScreenshotFile(attachmentFile)
          : attachmentFile
        fd.append('file', uploadFile)
      })
      const res = await authFetch(getLocalApiUrl(`/api/cases/${encodeURIComponent(caseId)}/activity-observations`), {
        method: 'POST',
        body: fd,
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to save observation')
      await onSaved?.(json.data)
      onClose?.()
    } catch (e) {
      setError(e.message || 'Failed to save observation')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40" onClick={() => !saving && onClose?.()}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <div className="text-sm font-semibold text-slate-800">Add activity observed</div>
            <div className="text-xs text-slate-500 mt-0.5">Record what you saw on CCTV or the floor — EGM, narrative, optional proof or floor link.</div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1 rounded hover:bg-slate-100">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error ? <div className="text-xs text-red-600 p-2 rounded bg-red-50 border border-red-100">{error}</div> : null}

          <label className="block">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Observed at</span>
            <input
              type="datetime-local"
              value={observedAt}
              onChange={(e) => setObservedAt(e.target.value)}
              className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2.5 py-2"
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">What was observed *</span>
            <textarea
              value={observationText}
              onChange={(e) => setObservationText(e.target.value)}
              onPaste={(e) => {
                const items = e.clipboardData?.items
                if (!items) return
                for (const item of items) {
                  if (item.type.startsWith('image/')) {
                    e.preventDefault()
                    const raw = item.getAsFile()
                    if (raw) {
                      setAttachmentFiles((prev) => [...prev, normalizePastedScreenshotFile(raw)])
                    }
                    break
                  }
                }
              }}
              placeholder="e.g. Patron moved from EGM 010100 to 010276; ticket in hand on CCTV… (paste screenshot here)"
              className="mt-1 w-full min-h-[88px] text-sm border border-slate-200 rounded-md px-2.5 py-2 resize-y"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">EGM</span>
              <input
                type="text"
                value={egm}
                onChange={(e) => setEgm(e.target.value)}
                placeholder="010100"
                className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2.5 py-2 font-mono"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Moved to EGM (optional)</span>
              <input
                type="text"
                value={egmTo}
                onChange={(e) => setEgmTo(e.target.value)}
                placeholder="010276"
                className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2.5 py-2 font-mono"
              />
            </label>
          </div>

          <div>
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1.5">Images or video (optional)</span>
            {attachmentFiles.length ? (
              <div className="space-y-1.5 mb-2">
                {attachmentFiles.map((f, idx) => (
                  <div key={`${f.name}-${idx}`} className="flex items-center gap-2 text-xs text-slate-600 p-2 bg-slate-50 border border-slate-200 rounded-md">
                    <span className="flex-1 truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setAttachmentFiles((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-red-600 font-semibold"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            <PasteScreenshotZone
              compact
              uploading={saving}
              onImageFile={(f) => setAttachmentFiles((prev) => [...prev, f])}
            />
            <label className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-slate-600 cursor-pointer">
              <input
                type="file"
                className="hidden"
                accept="image/*,video/*,.mp4,.mov,.webm"
                multiple
                onChange={(e) => {
                  const next = Array.from(e.target.files || [])
                  if (next.length) setAttachmentFiles((prev) => [...prev, ...next])
                  e.target.value = ''
                }}
              />
              <span className="py-1.5 px-3 rounded-md border border-slate-300 bg-white hover:bg-slate-50">Attach images / video</span>
            </label>
          </div>

          <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Link floor transaction (optional)</div>
            {linkedItem ? (
              <div className="flex items-center gap-2 text-xs text-slate-700 mb-2">
                <span className="flex-1">{floorItemLabel(linkedItem)}</span>
                <button type="button" onClick={() => setLinkedItem(null)} className="text-red-600 font-semibold">Clear</button>
              </div>
            ) : (
              <div className="text-xs text-slate-500 mb-2">Browse sessions, intervals, or payouts to attach floor data with this observation.</div>
            )}
            <button
              type="button"
              onClick={() => onBrowseFloorLink?.((item) => setLinkedItem(item))}
              className="text-xs py-1.5 px-3 rounded-md border font-semibold flex items-center gap-1 bg-white text-slate-700 border-slate-300"
            >
              <Table2 size={12} /> Browse &amp; select
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-xs py-2 px-4 rounded-md border border-slate-200 bg-white text-slate-600 font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="text-xs py-2 px-4 rounded-md border font-semibold flex items-center gap-1.5 disabled:opacity-50"
            style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Save observation
          </button>
        </div>
      </div>
    </div>
  )
}
